import { link } from 'fs'
import { useMemo } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { appPaths } from '@config'
import { Link } from '@assets-components'
import { Image, ImageData } from '@media-components'
import {
  SectionHeader,
  SectionHeaderData,
  SectionWrapper,
  type CardData,
} from '@components/common'
import { getStyles } from '@utils/styles'
import defaultStyles from './styles'

export type ComparisonChipsSectionData = {
  sectionHeaderData?: SectionHeaderData
  brandLogoData?: ImageData
  brandName?: string
  cardsData?: CardData[]
  darkBg?: boolean
  cardsPerColumn?: number
}

type ComparisonChipsSectionProps = ComparisonChipsSectionData & {
  darkBg?: boolean
  showDivider?: 'bottom' | 'top' | 'both' | 'none'
  alternateSectionBg?: boolean
}

export function ComparisonChipsSection({
  sectionHeaderData,
  cardsData,
  brandLogoData,
  brandName,
  darkBg = false,
  showDivider,
  cardsPerColumn = 5,
  alternateSectionBg = false,
}: ComparisonChipsSectionProps) {
  const styles = getStyles(defaultStyles)

  const columns = useMemo(() => {
    if (!cardsData?.length) return []
    const cols: CardData[][] = []
    for (let i = 0; i < cardsData.length; i += cardsPerColumn) {
      cols.push(cardsData.slice(i, i + cardsPerColumn))
    }
    return cols
  }, [cardsData])

  return (
    <SectionWrapper
      flexCol={{ xs: '20px', md: '32px' }}
      showDivider={showDivider}
      alternateBg={darkBg}
      alternateSectionBg={alternateSectionBg}
    >
      {sectionHeaderData && (
        <SectionHeader {...sectionHeaderData} darkBg={darkBg} />
      )}
      {cardsData && (
        <Box {...styles('cardWrapper')}>
          {columns.map((columnCards, colIndex) => (
            <Box key={colIndex} {...styles('columnWithDivider')}>
              <Box {...styles('column')}>
                {columnCards.map((card) => (
                  <Box
                    key={card.title}
                    {...styles('cardItem')}
                    {...(card.imageLink && {
                      component: Link,
                      href: card.imageLink,
                    })}
                  >
                    <Box {...styles('cardItemContent')}>
                      {card.imageData && (
                        <Image {...card.imageData} {...styles('brandLogo')} />
                      )}
                      <Typography variant="body2.600">{card.title}</Typography>
                    </Box>
                    <Typography variant="body2.600" color="text.secondary">
                      VS
                    </Typography>
                    <Box {...styles('cardItemContent')}>
                      {brandLogoData && (
                        <Image {...brandLogoData} {...styles('brandLogo')} />
                      )}
                      <Typography variant="body2.600">{brandName}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {columns.length > 0 && colIndex < columns.length - 1 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  {...styles('columnDivider')}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
    </SectionWrapper>
  )
}
